import React, { useState, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import Radio from '../Elements/Radio/Radio';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import Button from './Button/Button';
import Embed from './Embed/Embed';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Quote from './Quote/Quote';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import Table from './Table/Table';
import Ticker from './Ticker/Ticker';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';
import { changeUnits } from '../../actions/environment';
import { unique } from '../../utils/array';
import { percentToColumn } from '../../utils/columns';
import { purifier } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { deepMerge, deepClone, hasTemplateInObj } from '../../utils/object';
import { titleCase, urlify, camelCase, decode, cleanInternalLinks, rootUrl } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';
import s from './TemplateParser.scss';

const components = {
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfTicker: Ticker,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreQuote: Quote,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreTable: Table,
	coreVideo: Video,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ setup, customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[template],
			...attrsClassName.map((cN) => s[`${template}${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs?.layout?.type === 'flex') generalProps.style.display = 'flex';
	if (attrs?.layout?.justifyContent) generalProps.style.justifyContent = attrs.layout.justifyContent;
	if (attrs?.layout?.flexWrap) {
		generalProps.style.flexWrap = attrs.layout.flexWrap;
		if (attrs.layout.flexWrap === 'nowrap') generalProps.style.overflow = 'auto';
	}
	if (attrs.padding) {
		const padding = [...attrs.padding];
		if (padding.length === 4 && padding[1] !== padding[3] && setup.breakPoint === 'mobile') {
			const lowestPaddingSide = Math.min(padding[1], padding[3]);
			padding[1] = lowestPaddingSide;
			padding[3] = lowestPaddingSide;
		}
		generalProps.style.padding = padding.join(' ');
		generalProps.style.boxSizing = 'border-box';
	}
	if (attrs.contentJustification && attrs.contentJustification !== 'full') generalProps.style.justifyContent = attrs.contentJustification;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = 'u_fullWidth';
	if (attrs.align === 'wide') generalProps.className[generalProps.className.length] = 'u_wideWidth';

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (attrs) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs.href) {
		const link = cleanInternalLinks(attrs.href);
		const externalLink = /^(https?|mailto|tel)/.test(link);
		const anchorLink = /^#/.test(link);

		if (attrs.target || externalLink || anchorLink) {
			Element = 'a';
			extraProps.href = link;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = link.replace(/\/+$/, '');
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup) => {
	if (isRestricted(setup, attrs)) return null;
	if (components[template]) return components[template];
	if (containers[template]) return containers[template];

	console.log('DEBUG: missing template', template, containers, components);
	return null;
};

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

containers.acfImageGroup = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					if (tpl === 'content') return null;
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.acfStickyContainer = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			<div className={s.acfStickyContainer_inner}>
				{
					Array.isArray(content) && content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						if (tpl === 'content') return null;
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
		</div>
	);
};

containers.acfMeasurementContainer = ({ attrs = {}, content = [], setup, template, sizes = [], modelInfo = {}, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });
	const title = Array.isArray(content) ? content.filter((childSection) => !!childSection.content).map((childSection) => childSection?.content?.measurementContainer)[0] : content?.measurementContainer;
	const changeUnitsHandler = (e) => {
		e.preventDefault();
		const units = e.currentTarget.getAttribute('href').slice(1);
		props.changeUnits(units);
	};

	const formattedSizes = sizes.map((item) => ({
		key: item.item,
		value: item.name,
		info: item.stock <= 3 && item.stock > 0 ? item.stock : '',
		disabled: item.stock === 0,
		selected: props.isSizeGuide === item.name.toUpperCase(),
	}));

	const updateSizeHandler = (id, item) => {
		props.updateSelected(item);

		const selectedItem = sizes.filter((size) => size.item === item);

		if (typeof window.ssp !== 'undefined' && selectedItem[0]) {
			window.ssp('set', 'product', {
				productId: selectedItem[0].productId,
				size: selectedItem[0].name.toUpperCase(),
				variantId: selectedItem[0].sku,
			});
		}
	};

	return (
		<div {...extraProps}>
			<div className={s.acfMeasurementContainer_header}>
				{ title && <strong className={s.acfMeasurementContainer_header_title}>{ `${translate(setup, title.title)} (${translate(setup, setup.units)})` }</strong> }
				<div className={s.acfMeasurementContainer_header_units}>
					<a className={[s.acfMeasurementContainer_header_unit, (setup.units === 'cm' ? s.acfMeasurementContainer_header_unit__selected : null)].join(' ')} href="#cm" onClick={changeUnitsHandler}>
						<span>{ translate(setup, 'cm') }</span>
					</a>
					<a className={[s.acfMeasurementContainer_header_unit, (setup.units === 'inches' ? s.acfMeasurementContainer_header_unit__selected : null)].join(' ')} href="#inches" onClick={changeUnitsHandler}>
						<span>{ translate(setup, 'inches') }</span>
					</a>
				</div>
			</div>
			{
				Object.keys(formattedSizes).length > 0 && (
					<div className={s.acfMeasurementContainer_sizes}>
						<Radio variantClasses="radio__size" id="measurement-sizes" options={formattedSizes} selected={props.isSizeGuide} inputChange={updateSizeHandler} />
					</div>
				)
			}
			{
				Object.keys(modelInfo).length > 0 && (
					<ul className={s.acfMeasurementContainer_modelInfo}>
						{
							Object.keys(modelInfo).map((info) => {
								const mI = modelInfo[info];
								return (
									<Fragment key={mI.title}>
										<li><span>{ mI.title.slice(0, mI.title.indexOf('is ') + 3) }</span><strong>{ mI.title.slice(mI.title.indexOf('is ') + 3) }</strong></li>
										<li><span>{ translate(setup, 'modelWearing') }</span> <strong>{ translate(setup, 'modelSize').replace('%size%', mI.value) }</strong></li>
									</Fragment>
								);
							})
						}
					</ul>
				)
			}
			{
				Array.isArray(content) && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					if (tpl === 'content') return null;
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} measurementContainer {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.acfArticleList = ({ storyListing, styleListing, content, setup }) => {
	const list = content.list || 'story';
	const listingType = list === 'story' ? storyListing : styleListing;
	if (!listingType?.content) return null;
	let selectedArticles = [];
	const keyedArticleListing = listingType.content.reduce((acc, article) => ({ ...acc, [`${article.meta.default.id}`]: article }), {});
	const taggedArticleListing = {};
	listingType.content.forEach((article) => {
		if (article.meta.default.tags && Object.keys(article.meta.default.tags).length > 0) {
			Object.keys(article.meta.default.tags).forEach((t) => {
				if (!taggedArticleListing[t]) taggedArticleListing[t] = {};
				taggedArticleListing[t][`${article.meta.default.id}`] = article;
			});
		}
	});

	if (content.individual) {
		selectedArticles = [...selectedArticles, ...content.individual];
	}
	if (content.tags) {
		selectedArticles = [...selectedArticles, ...content.tags.reduce((acc, tag) => [...acc, ...(Object.keys(taggedArticleListing?.[tag]) || [])], [])];
	} else {
		selectedArticles = [...selectedArticles, ...Object.keys(keyedArticleListing)];
	}
	selectedArticles = unique(selectedArticles);
	if (content.sort) {
		selectedArticles = selectedArticles.sort((a, b) => {
			const sA = keyedArticleListing[a].meta.default;
			const sB = keyedArticleListing[b].meta.default;
			if (content.sort === 'date') {
				if (sA.published === sB.published) return 0;
				return sA.published > sB.published ? 1 : -1;
			}
			if (content.sort === 'menu_order') {
				if (sA.order === sB.order) return 0;
				return sA.order > sB.order ? 1 : -1;
			}
			return sA.uri.localeCompare(sB.uri);
		});
		if (parseInt(content.sortDirection, 10) === -1) selectedArticles = selectedArticles.reverse();
	}
	if (content.limit) selectedArticles = selectedArticles.slice(0, content.limit);

	return (
		<>
			{ selectedArticles.length > 0 && content.identifier === 'start' && <Link className={s.acfArticleList_readMore} to={prefix(setup, rootUrl(keyedArticleListing?.[selectedArticles[0]]?.meta?.default?.fullUri))}>{ translate(setup, 'readMore') }</Link> }
			<div className={[s.acfArticleList, (content.variation ? s[`acfArticleList__${content.variation}`] : null), (content.identifier ? s[`acfArticleList__${content.identifier}`] : null)].join(' ')}>
				{
					selectedArticles.length > 0 && selectedArticles.map((articleId, i) => {
						const article = keyedArticleListing[articleId];
						const columns = content.variation === 'default' && i < 2 ? '6' : '3';
						const lazyLoad = content.variation === 'default' && i < 2 ? 'eager' : 'lazy';

						return (
							<section key={article.meta.default.uri} className={s.acfArticleList_section}>
								{
									article.sections.length > 0 && list === 'story' && (
										<Link className={s.acfArticleList_section_inner} to={prefix(setup, article.meta.default.fullUri)}>
											<TemplateParser sections={article.sections} setup={setup} article={list} column={columns} lazyLoad={lazyLoad} />
										</Link>
									)
								}
								{
									article.sections.length > 0 && list !== 'story' && (
										<div className={s.acfArticleList_section_inner}>
											<TemplateParser sections={article.sections} setup={setup} article={list} />
										</div>
									)
								}
							</section>
						);
					})
				}
			</div>
		</>
	);
};

containers.ccBlocksAccordion = ({ attrs = {}, content = [], setup, template, sectionItem = false, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} sectionItem={`${sectionItem ? `${sectionItem}.` : ''}${i + 1}`} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs = {}, content = [], setup, sectionItem, template, ...props }) => {
	const customProps = {};

	const [accordianToggled, toggleAccordian] = useState(attrs.open || false);
	const toggleAccordianHandler = (e) => {
		e.preventDefault();
		toggleAccordian((prevState) => !prevState);
	};

	if (accordianToggled) {
		customProps.className = [s.ccBlocksAccordionItem__expanded];
	}

	const extraProps = generalSettings({ setup, customProps, attrs, template });


	return (
		<div {...extraProps}>
			<button aria-controls={urlify(attrs.title.slice(0, 50))} aria-expanded={accordianToggled} type="button" onClick={toggleAccordianHandler} className={[s.ccBlocksAccordionItem_title, (accordianToggled ? s.ccBlocksAccordionItem_title__expanded : null)].join(' ')}>
				<span>{ decode(attrs.title) }</span>
				<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0 3.228L8.13034e-08 5.088L1.968 3.6L3.468 2.4L3.468 8.748H5.088L5.088 2.424L6.564 3.6L8.544 5.088V3.228L4.272 0L0 3.228Z" fill="black" />
				</svg>
			</button>
			<div aria-hidden={!accordianToggled} id={urlify(attrs.title.slice(0, 50))} className={s.ccBlocksAccordionItem_content}>
				<div className={content.filter((childSection) => Object.keys(childSection)[0] === 'ccBlocksAccordion').length === 0 ? s.ccBlocksAccordionItem_content_inner : s.ccBlocksAccordionItem_content_outer }>
					{
						content.map((childSection) => {
							const tpl = Object.keys(childSection)[0];
							const nextContent = childSection[tpl];
							const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
							if (!ChildElement) return null;
							return <ChildElement template={tpl} key={nextContent.key} sectionItem={sectionItem} setup={setup} {...nextContent} {...props} />;
						})
					}
				</div>
			</div>
		</div>
	);
};

containers.ccBlocksImageHotspot = ({ attrs = {}, content = [], uid, setup, template, ...props }) => {
	const customProps = {};

	if (props.isSizeGuide) {
		customProps.className = [s.ccBlocksImageHotspot__sizeGuide];
	}

	const extraProps = generalSettings({ setup, customProps, attrs, template });
	const [pinExpanded, togglePin] = useState(false);

	const handleClearPinClick = (e) => {
		e.preventDefault();
		togglePin(false);
	};

	const handlePinClick = (e) => {
		e.preventDefault();
		const index = parseInt(e.currentTarget.dataset.index, 10);
		togglePin((prevState) => (prevState === index ? false : index));
	};

	return (
		<div {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...props} />
			{ pinExpanded !== false && <button className={s.ccBlocksImageHotspot_clear} type="button" onClick={handleClearPinClick} aria-label="Clear expanded pins" /> }
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement || (props.isProduct && tpl === 'ccBlocksImageHotspotPin')) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} index={i} handlePinClick={handlePinClick} pinExpanded={pinExpanded} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksImageHotspotPin = ({ attrs = {}, content = [], uid, setup, template, index, handlePinClick, pinExpanded, ...props }) => {
	const customProps = {};
	let mainContent = deepClone(content);
	let auxiliaryContent = [];

	const { left = 50, top = 50, layout = 'bottom-right' } = attrs;
	const style = {
		left: `${left}%`,
		top: `${top}%`,
	};
	if (props.isSizeGuide) {
		template = 'ccBlocksImageHotspotSizePin';
		mainContent = deepClone(content.slice(0, 2));
		auxiliaryContent = deepClone(content.slice(2));
	}
	if (setup.breakPoint !== 'mobile' || props.isSizeGuide) customProps.style = style;
	customProps.className = [s[`ccBlocksImageHotspotPin__${camelCase(layout)}`]];
	if (pinExpanded === index) customProps.className.push(s[`${template}__selected`]);

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<>
			<div {...extraProps}>
				<button type="button" className={s[`${template}_pin`]} onClick={handlePinClick} data-index={index} style={setup.breakPoint === 'mobile' ? style : {}}>
					<span />
				</button>
				<div className={[s[`${template}_content`], props.isSizeGuide && auxiliaryContent.length > 0 ? s[`${template}_content__hasAuxiliaryContent`] : null].join(' ')}>
					{
						mainContent.map((childSection) => {
							const tpl = Object.keys(childSection)[0];
							const nextContent = childSection[tpl];
							const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
							if (!ChildElement) return null;
							return <ChildElement template={tpl} isHotspot key={nextContent.key} setup={setup} {...nextContent} {...props} />;
						})
					}
				</div>
			</div>
			{
				props.isSizeGuide && (
					<div className={s[`${template}_auxiliaryContent`]}>
						{
							auxiliaryContent.map((childSection) => {
								const tpl = Object.keys(childSection)[0];
								const nextContent = childSection[tpl];
								const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
								if (!ChildElement) return null;
								return <ChildElement template={tpl} isHotspot key={nextContent.key} setup={setup} {...nextContent} {...props} />;
							})
						}
					</div>
				)
			}
		</>
	);
};

containers.ccBlocksPuff = ({ attrs = {}, content = [], uid, setup, template, ...props }) => {
	const [customProps, settings] = heightSettings({ attrs, setup });
	const [Element, elementProps] = getElement(attrs);
	customProps.className = [s[`container__${camelCase(attrs.layout || 'top left')}`]];
	if (attrs.animate) customProps.className.push('u_animate');
	const extraProps = generalSettings({ setup, customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} />
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					if (attrs.animate && tpl === 'coreGroup') nextContent.animate = true;
					if (tpl === 'coreColumns') nextContent.puffSubHeader = true;
					return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
				})
			}
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

containers.ccBlocksSlider = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite: 1,
		hint: attrs?.hint || false,
		autoplay: attrs?.autoplay === false ? false : attrs.transitionDelay || 3000,
		interactionTypes: ['touch'],
		adaption: 'content',
	};
	let Wrapper;
	if (attrs.arrowColor) {
		if (typeof customProps.style === 'undefined') customProps.style = {};
		customProps.style.color = attrs.arrowColor;
	}

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	if (attrs?.freeFlow && attrs?.align === 'full') {
		Wrapper = ({ children }) => (
			<div className={s.container__ccBlocksSlider_inner}>
				<button className={s.container__ccBlocksSlider_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
					<svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L11 9L1 17" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	} else {
		Wrapper = ({ children }) => (
			<Slider {...sliderAttrs}>
				{ children }
			</Slider>
		);
	}

	return (
		<div {...extraProps}>
			<Wrapper>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</Wrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
	};
	if (props.inline) customProps.className.push(s.coreButtons__plpButtons);
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumn = ({ attrs = {}, content = [], setup, template, column, ...props }) => {
	const customProps = {
		className: [],
	};

	if (attrs?.width && attrs.width.indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { maxWidth: attrs.width, width: '50%' };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];
	if (!attrs?.width) customProps.className.push('u_width__grow');
	if (props.isSizeGuide) customProps.className.push(s.coreColumn__sizeGuide);

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumns = ({ attrs = {}, content = [], setup, template, puffSubHeader = false, ...props }) => {
	const customProps = {
		className: ['u_columns'],
	};

	if (typeof attrs?.isStackedOnMobile !== 'undefined' && attrs.isStackedOnMobile === 0) customProps.className = [s.coreColumns__columnsRowMobile];

	const hasProducts = hasTemplateInObj(content, 'acfProducts');
	if (hasProducts.length > 0) customProps.className.push('u_columns__products');
	const initialColumnWidths = percentToColumn(content);
	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					if (puffSubHeader && i > 0) props.puffSubHeader = true;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} column={props?.column !== undefined ? false : initialColumnWidths[i]} />;
				})
			}
		</div>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} />;

	const extraProps = generalSettings({ setup, customProps, attrs, template });
	let Wrapper = ({ children }) => (
		<ul {...extraProps}>
			{ children }
		</ul>
	);

	if (attrs?.ordered) {
		Wrapper = ({ children }) => (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreGroup = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (attrs.layout) customProps.className = [s[`coreGroup__${camelCase(attrs.layout)}`]];
	const extraProps = generalSettings({ setup, customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainer = ({ attrs = {}, content = [], template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	let Wrapper = ({ children }) => children;

	// Add wrappers
	if (!props.inline && attrs?.align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		Wrapper = ({ children }) => (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<Wrapper>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</Wrapper>
		</ErrorBoundary>
	);
};

const TemplateParser = ({ sections, setup, variantClasses = false, skipFirst = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			if (isRestricted(setup, section[template].attrs)) return;
			const ChildElement = containers[template] || NoContainer;
			if (ChildElement) {
				const Wrapper = ({ children }) => (classes.length > 0 ? <div className={classes.join(' ')}>{ children }</div> : children);
				const nextContent = section[template];
				templateContents[templateContents.length] = (
					<Wrapper key={nextContent.key}>
						<ChildElement template={template} setup={setup} first={skipFirst ? false : first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</Wrapper>
				);

				// First section that is visible to customer.
				first = false;
			}
		}
	});

	return templateContents;
};

const mapState = ({ listing }, props) => ({
	storyListing: listing?.story,
	styleListing: listing?.style,
	...props,
});

export default hot(module)(connect(mapState, { changeUnits })(TemplateParser));
